import { Controller } from 'aurelia-framework';
import { RenderInstruction } from 'aurelia-validation';

import { Field } from '~/components/field/common/field';

/**
 * This render can be only used on validation results related to elements that are Aurelia Element themselves or
 * wrapped into a Aurelia Element. For instance, basically in `Field` custom element with validation applied, validation
 * results are related to a `input` element, and this `input` element is wrapped in a `a-field` Aurelia Element.
 *
 * The key idea here is to find the closest Aurelia Element of actual validated element and set its validationMessage
 * property.
 *
 * The actual validated element can be pure DOM element or Custome Element.
 * If it is DOM element, ensure it has `data-field-id` attribute
 * If it is custom element, ensure it's view model has a dataFieldId property
 *
 * TODO: York - test
 */

export class ValidationMessageRenderer {
	/**
	 * getClosestFieldElement will walk up the DOM tree and try to find
	 * what field element (ie. <a-text>) this element belongs to.
	 */
	private static getClosestFieldElement = (
		element: HTMLElement,
	): Element | null => {
		let parent: Element = element;
		let parentFound;
		while (parent.parentNode) {
			parent = parent.parentNode as Element;
			if (parent && parent.au) {
				for (const componentName in parent.au) {
					if (!parent.au.hasOwnProperty(componentName)) {
						continue;
					}
					const controller = parent.au[componentName];
					if (controller && controller.viewModel instanceof Field) {
						parentFound = parent;
						break;
					}
				}
			}
		}
		if (!parentFound) {
			return null;
		}
		return parentFound;
	};

	/**
	 * getFieldViewModel will get the Field viewModel from an field element's controller.
	 * ie. this method expects a <a-text>, <a-select> element and will retrieve the viewModel from the element.
	 */
	private static getFieldViewModel = (fieldElem: Element): Field => {
		let fieldViewModel: Field | null = null;
		if (fieldElem && fieldElem.au) {
			for (const name in fieldElem.au) {
				if (!fieldElem.au.hasOwnProperty(name)) {
					continue;
				}
				const controller = fieldElem.au[name];
				if (controller && controller.viewModel instanceof Field) {
					fieldViewModel = controller.viewModel;
					break;
				}
			}
		}
		return fieldViewModel;
	};

	public render(instruction: RenderInstruction) {
		instruction.unrender.forEach(({ result, elements }) => {
			elements.forEach((element: HTMLElement) => {
				const fieldElem =
					ValidationMessageRenderer.getClosestFieldElement(element);
				if (!fieldElem) {
					return;
				}
				const fieldViewModel =
					ValidationMessageRenderer.getFieldViewModel(fieldElem);
				if (fieldViewModel) {
					fieldViewModel.validationMessage = '';
				}
			});
		});
		instruction.render.forEach(({ result, elements }) => {
			elements.forEach((element: HTMLElement) => {
				if (result.valid) {
					return;
				}
				const fieldElem =
					ValidationMessageRenderer.getClosestFieldElement(element);
				if (!fieldElem) {
					return;
				}
				const fieldViewModel =
					ValidationMessageRenderer.getFieldViewModel(fieldElem);
				if (fieldViewModel) {
					if (fieldViewModel.validationMessage) {
						// Add seperator between each validation message
						fieldViewModel.validationMessage += ` | `;
					}
					fieldViewModel.validationMessage += result.message;
				}
			});
		});
	}
}
